import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { clsx } from 'clsx'
import { api, ApiError, type AppRow, type PermissionPreview } from '../lib/api'
import { formatRelative } from '../lib/format'
import { Spinner, Tag, ErrorNote, InfoNote } from '../components/ui/Primitives'

interface UserRow {
  id: string
  email: string
  name: string
  isSuperAdmin: boolean
  disabledAt: string | null
  lastLoginAt: string | null
  _count: { grants: number }
}

interface Role {
  id: string
  name: string
  description: string
  capabilities: string[]
}

export default function Users() {
  const [creating, setCreating] = useState(false)
  const { data: users, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UserRow[]>('/users'),
  })

  if (isLoading) return <Spinner className="h-6 w-6" />

  return (
    <div className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Usuarios</h1>
          <p className="hint mt-0.5">{users?.length ?? 0} en el panel</p>
        </div>
        <button className="btn-primary" onClick={() => setCreating(true)}>Nuevo usuario</button>
      </header>

      <div className="card divide-y divide-slate-800/60 p-0">
        {(users ?? []).map((u) => (
          <div key={u.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">{u.name}</p>
              <p className="hint truncate">{u.email}</p>
            </div>
            {u.isSuperAdmin && <Tag tone="amber">superadministrador</Tag>}
            {u.disabledAt && <Tag tone="slate">deshabilitado</Tag>}
            <span className="hint w-28 text-right">{u._count.grants} permiso(s)</span>
            <span className="hint w-32 text-right">{formatRelative(u.lastLoginAt)}</span>
          </div>
        ))}
      </div>

      {creating && <NewUserWizard onClose={() => setCreating(false)} />}
    </div>
  )
}

/**
 * Alta de usuario en 5 pasos.
 *
 * El paso 3 es el que pidió Fernando: elegir explícitamente a qué
 * aplicaciones (y a qué entornos dentro de cada una) tiene acceso.
 * El paso 5 muestra el resultado calculado POR EL SERVIDOR, con la misma
 * función que después decide cada request.
 */
function NewUserWizard({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [step, setStep] = useState(1)
  const [form, setForm] = useState({ name: '', email: '', password: '' })
  const [roleId, setRoleId] = useState<string>('')
  /** clave: deploymentId | `app:${appId}` → seleccionado */
  const [picked, setPicked] = useState<Record<string, boolean>>({})
  const [denyProd, setDenyProd] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const { data: apps } = useQuery({ queryKey: ['apps'], queryFn: () => api.get<AppRow[]>('/apps') })
  const { data: roles } = useQuery({ queryKey: ['roles'], queryFn: () => api.get<Role[]>('/users/roles') })

  const grants = useMemo(() => {
    const out: Array<{ scopeType: string; scopeId: string; capabilities: string[]; effect: string }> = []
    for (const [key, on] of Object.entries(picked)) {
      if (!on) continue
      if (key.startsWith('app:')) {
        out.push({ scopeType: 'APP', scopeId: key.slice(4), capabilities: [], effect: 'ALLOW' })
      } else {
        out.push({ scopeType: 'DEPLOYMENT', scopeId: key, capabilities: [], effect: 'ALLOW' })
      }
    }
    if (denyProd) {
      for (const app of apps ?? []) {
        // Solo tiene sentido denegar producción si el acceso vino por la app
        // entera; si eligieron el despliegue de producción a mano, respetamos
        // esa decisión explícita.
        if (!picked[`app:${app.id}`]) continue
        for (const dep of app.deployments) {
          if (dep.environment !== 'PRODUCTION') continue
          if (picked[dep.id]) continue
          out.push({
            scopeType: 'DEPLOYMENT', scopeId: dep.id, effect: 'DENY',
            capabilities: ['instance.power', 'instance.deploy', 'instance.files.write', 'instance.env.write', 'instance.db.write'],
          })
        }
      }
    }
    return out
  }, [picked, denyProd, apps])

  const preview = useQuery({
    queryKey: ['preview', roleId, JSON.stringify(grants)],
    queryFn: () => api.post<PermissionPreview>('/users/permissions/preview', { roleId: roleId || undefined, grants }),
    enabled: step === 5 && grants.length > 0,
  })

  const create = useMutation({
    mutationFn: () => api.post('/users', { ...form, roleId: roleId || undefined, grants }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] })
      onClose()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'No se pudo crear el usuario'),
  })

  const selectedCount = Object.values(picked).filter(Boolean).length

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl border bg-slate-900 shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="font-semibold">Nuevo usuario · paso {step} de 5</h2>
          <button className="text-slate-400 hover:text-slate-200" onClick={onClose} aria-label="Cerrar">✕</button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-auto px-6 py-5">
          {error && <ErrorNote>{error}</ErrorNote>}

          {step === 1 && (
            <>
              <div>
                <label className="label">Nombre</label>
                <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
              </div>
              <div>
                <label className="label">Email</label>
                <input className="input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <label className="label">Contraseña inicial</label>
                <input className="input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                <p className="hint mt-1">Mínimo 12 caracteres, con mayúscula, minúscula y número.</p>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <p className="text-sm text-slate-400">
                El rol define <em>qué puede hacer</em>. En el paso siguiente elegís <em>sobre qué aplicaciones</em>.
              </p>
              {(roles ?? []).map((r) => (
                <label key={r.id} className={clsx(
                  'flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors',
                  roleId === r.id ? 'border-blue-500 bg-blue-500/10' : 'hover:bg-slate-800/50',
                )}>
                  <input type="radio" name="rol" className="mt-1" checked={roleId === r.id} onChange={() => setRoleId(r.id)} />
                  <div>
                    <p className="font-medium">{r.name}</p>
                    <p className="hint mt-0.5">{r.description}</p>
                    <p className="hint mt-1">{r.capabilities.length} permisos</p>
                  </div>
                </label>
              ))}
            </>
          )}

          {step === 3 && (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-400">Elegí a qué aplicaciones y entornos le das acceso.</p>
                <div className="flex gap-2">
                  <button className="btn-ghost text-xs" onClick={() => {
                    const all: Record<string, boolean> = {}
                    for (const a of apps ?? []) all[`app:${a.id}`] = true
                    setPicked(all)
                  }}>Marcar todas</button>
                  <button className="btn-ghost text-xs" onClick={() => setPicked({})}>Ninguna</button>
                </div>
              </div>

              {(apps ?? []).map((a) => (
                <div key={a.id} className="rounded-lg border p-4">
                  <label className="flex cursor-pointer items-center gap-3">
                    <input
                      type="checkbox"
                      checked={Boolean(picked[`app:${a.id}`])}
                      onChange={(e) => setPicked({ ...picked, [`app:${a.id}`]: e.target.checked })}
                    />
                    <span className="font-medium">{a.slug}</span>
                    <span className="hint">{a.name}</span>
                  </label>

                  {a.deployments.length > 0 && (
                    <div className="ml-7 mt-3 flex flex-wrap gap-3">
                      {a.deployments.map((dep) => (
                        <label key={dep.id} className="flex cursor-pointer items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={Boolean(picked[dep.id]) || Boolean(picked[`app:${a.id}`])}
                            disabled={Boolean(picked[`app:${a.id}`])}
                            onChange={(e) => setPicked({ ...picked, [dep.id]: e.target.checked })}
                          />
                          <span className={clsx(picked[`app:${a.id}`] && 'text-slate-500')}>{dep.name}</span>
                          {dep.protected && <Tag tone="amber">protegida</Tag>}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <p className="hint">{selectedCount} selección(es)</p>
            </>
          )}

          {step === 4 && (
            <>
              <p className="text-sm text-slate-400">Ajustes finos sobre lo elegido.</p>
              <label className="flex cursor-pointer items-start gap-3 rounded-lg border p-4">
                <input type="checkbox" className="mt-1" checked={denyProd} onChange={(e) => setDenyProd(e.target.checked)} />
                <div>
                  <p className="font-medium">No dejarlo tocar producción</p>
                  <p className="hint mt-0.5">
                    Cuando el acceso se da por aplicación entera, se agrega una regla que le quita
                    arrancar, parar, desplegar y escribir en los entornos de producción. Va a poder
                    verlos, pero no modificarlos.
                  </p>
                </div>
              </label>
            </>
          )}

          {step === 5 && (
            <>
              <p className="text-sm text-slate-400">
                Esto es lo que va a poder hacer. Lo calcula el servidor con la misma función que
                después decide cada pedido, así que no hay sorpresas.
              </p>
              {preview.isLoading && <Spinner />}
              {preview.data && (
                <>
                  {preview.data.access.map((a) => (
                    <div key={a.target} className="rounded-lg border p-3">
                      <p className="text-sm font-medium">
                        {a.target} {a.protected && <Tag tone="amber">protegida</Tag>}
                      </p>
                      <p className="hint mt-1">{a.capabilities.length} permisos</p>
                    </div>
                  ))}
                  {preview.data.noAccessTo.length > 0 && (
                    <p className="text-sm text-slate-400">
                      <strong>No va a ver:</strong> {preview.data.noAccessTo.join(', ')}
                    </p>
                  )}
                  {preview.data.warnings.map((w, i) => <InfoNote key={i}>{w}</InfoNote>)}
                </>
              )}
              {grants.length === 0 && <ErrorNote>No seleccionaste ninguna aplicación. Volvé al paso 3.</ErrorNote>}
            </>
          )}
        </div>

        <div className="flex items-center justify-between border-t px-6 py-4">
          <button className="btn-ghost" onClick={() => (step === 1 ? onClose() : setStep(step - 1))}>
            {step === 1 ? 'Cancelar' : 'Atrás'}
          </button>
          {step < 5 ? (
            <button
              className="btn-primary"
              disabled={step === 1 && (!form.name || !form.email || form.password.length < 12)}
              onClick={() => setStep(step + 1)}
            >
              Siguiente
            </button>
          ) : (
            <button className="btn-primary" disabled={create.isPending || grants.length === 0} onClick={() => create.mutate()}>
              {create.isPending && <Spinner />} Crear usuario
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
